import React from 'react';
import { BadgeCheck} from 'lucide-react';
import type { SGCUser } from '@/context/AuthContext';

interface ProfileModalProps {
  open: boolean;
  user: SGCUser | null;
  initials: string;
  departamento: string | null;
  avatarGone: boolean;
  avatarHint: string | null;
  modalAvatarRef: React.RefObject<HTMLButtonElement>;
  onAvatarClick: (el: HTMLElement) => void;
  onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  user,
  initials,
  departamento,
  avatarGone,
  avatarHint,
  modalAvatarRef,
  onAvatarClick,
  onClose,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-[1px] flex items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-3xl border border-white/20 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 shadow-2xl overflow-hidden">
        <div className="relative px-6 pt-7 pb-5 border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.25),transparent_50%)] pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="flex flex-col items-center">
              <button
                ref={modalAvatarRef}
                type="button"
                onClick={(e) => onAvatarClick(e.currentTarget)}
                title="Toca para jugar"
                className={`h-20 w-20 rounded-2xl overflow-hidden border border-white/20 shadow-lg transition-all duration-300 ${avatarGone ? 'opacity-0 pointer-events-none translate-x-10' : 'opacity-100'}`}
              >
                <div className="h-full w-full">
                  {user?.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt={user?.fullName || 'Usuario'}
                      className="h-full w-full object-cover select-none"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-blue-500 to-violet-600 text-white text-2xl font-bold flex items-center justify-center select-none">
                      {initials}
                    </div>
                  )}
                </div>
              </button>
              <div className="h-5 mt-1">
                {avatarHint && <p className="text-[11px] text-indigo-200/90">{avatarHint}</p>}
              </div>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-white truncate">{user?.fullName || 'Usuario'}</h2>
              <p className="text-sm text-indigo-200/90 truncate">{user?.roleSGC}</p>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-300/20 bg-emerald-500/10 text-emerald-200 text-xs">
                <BadgeCheck className="h-3.5 w-3.5" />
                Activo en SGC
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Email</p>
            <p className="mt-1 text-sm text-white break-all">{user?.email || '—'}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Nómina</p>
            <p className="mt-1 text-sm text-white">{user?.nomina || '—'}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Departamento</p>
            <p className="mt-1 text-sm text-white">{departamento || 'No definido'}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">UID</p>
            <p className="mt-1 text-xs text-slate-200 break-all">{user?.uid || '—'}</p>
          </div>
        </div>

        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-white/15 text-slate-200 hover:bg-white/10 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
